import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Button, Tooltip, Table, Modal, Space, message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, GiftOutlined } from '@ant-design/icons';
import { api } from '../../services/api';
import { useClient } from '../../context/ClientContext';
import { SkStyleTag, skeletonColumns, skeletonRows } from '../../shared/components/TableSkeleton';

const { Text } = Typography;

const generarNombre = (tipo, valor, montoMinimo) => {
  if (!tipo) return '';
  const min = montoMinimo > 0 ? ` en compras mayores a $${Number(montoMinimo).toFixed(0)}` : '';
  switch (tipo.codigo) {
    case 'envio_gratis': return `Envío gratis${min}`;
    case 'descuento_fijo': return `$${Number(valor).toFixed(0)} de descuento${min}`;
    case 'descuento_porcentaje': return `${valor}% de descuento${min}`;
    case 'precio_fijo': return `A $${Number(valor).toFixed(0)}${min}`;
    case '2x1': return `2x1${min}`;
    default: return tipo.nombre;
  }
};

const formatValor = (tipo, valor) => {
  if (!tipo) return '';
  if (tipo.codigo === 'envio_gratis') return 'Envío gratis';
  if (tipo.codigo === 'descuento_fijo') return `- $${Number(valor).toFixed(0)}`;
  if (tipo.codigo === 'descuento_porcentaje') return `- ${valor}%`;
  if (tipo.codigo === 'precio_fijo') return `$ ${Number(valor).toFixed(0)}`;
  if (tipo.codigo === '2x1') return '2x1';
  return '';
};

const CircleBtn = ({ icon, onClick, danger, title, disabled }) => (
  <Tooltip title={title}>
    <button
      type="button" onClick={onClick} disabled={disabled}
      style={{
        width: 32, height: 32, border: `1px solid ${danger ? '#fca5a5' : '#e5e7eb'}`,
        borderRadius: '50%', background: '#fff',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        color: danger ? '#ef4444' : '#374151', opacity: disabled ? 0.45 : 1,
        transition: 'border-color 0.15s', fontSize: 14, flexShrink: 0,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.borderColor = danger ? '#ef4444' : '#374151'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = danger ? '#fca5a5' : '#e5e7eb'; }}
    >
      {icon}
    </button>
  </Tooltip>
);

export const AdminPromociones = () => {
  const { theme, clientPath } = useClient();
  const primary = theme?.primaryColor || '#0f172a';
  const primaryText = theme?.primaryTextColor || '#fff';
  const navigate = useNavigate();

  const [promos, setPromos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    const [resPromos, resTipos] = await Promise.all([
      api.promociones.admin.getAll(),
      api.promociones.admin.getTipos(),
    ]);
    if (resPromos.status === 'success') setPromos(resPromos.data || []);
    if (resTipos.status === 'success') setTipos(resTipos.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await api.promociones.admin.eliminar(confirmDel.id);
      message.success('Promoción eliminada');
      setConfirmDel(null);
      cargar();
    } finally {
      setDeleting(false);
    }
  };

  const handleToggle = async (p) => {
    if (toggling) return;
    setToggling(p.id);
    try {
      await api.promociones.admin.toggle(p.id);
      await cargar();
    } finally {
      setToggling(null);
    }
  };

  const promoColumns = [
    {
      title: 'Promoción', key: 'nombre', minWidth: 280,
      render: (_, p) => {
        const tipo = tipos.find(t => t.id === p.id_promocion);
        const nombre = generarNombre(tipo, p.valor, p.monto_minimo);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: p.activa ? 1 : 0.55 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, flexShrink: 0,
              background: p.activa ? '#f0fdf4' : '#f9fafb',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: p.activa ? '#16a34a' : '#9ca3af', fontSize: 18,
            }}>
              <GiftOutlined />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontWeight: 600, fontSize: 14, color: primary,
                cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden',
                textOverflow: 'ellipsis', maxWidth: 320,
              }} onClick={() => navigate(clientPath(`/admin/promociones/${p.id}`))}>
                {nombre}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>{tipo?.nombre}</Text>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Descuento', key: 'valor', width: 140,
      render: (_, p) => {
        const tipo = tipos.find(t => t.id === p.id_promocion);
        return <Text style={{ fontSize: 13 }}>{formatValor(tipo, p.valor)}</Text>;
      },
    },
    {
      title: 'Compra mínima', key: 'monto', width: 140,
      render: (_, p) => (
        <Text style={{ fontSize: 13 }}>
          {p.monto_minimo > 0 ? `$ ${Number(p.monto_minimo).toFixed(0)}` : '—'}
        </Text>
      ),
    },
    {
      title: 'Alcance', key: 'alcance', width: 130,
      render: (_, p) => {
        const a = p.alcance || 'tienda';
        const label = a === 'tienda' ? 'Toda la tienda'
          : a === 'categorias' ? `${(p.categoria_ids || []).length} categoría${(p.categoria_ids || []).length !== 1 ? 's' : ''}`
          : `${(p.producto_ids || []).length} producto${(p.producto_ids || []).length !== 1 ? 's' : ''}`;
        return <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>;
      },
    },
    {
      title: 'Estado', key: 'estado', width: 120,
      render: (_, p) => {
        const isToggling = toggling === p.id;
        return (
          <Tooltip title={isToggling ? '' : (p.activa ? 'Doble click para desactivar' : 'Doble click para activar')}>
            <span
              onDoubleClick={() => handleToggle(p)}
              style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '3px 10px', borderRadius: 20,
                border: `1.5px solid ${p.activa ? '#16a34a' : '#9ca3af'}`,
                background: p.activa ? '#f0fdf4' : '#f9fafb',
                color: p.activa ? '#16a34a' : '#9ca3af',
                fontSize: 12, fontWeight: 600,
                cursor: isToggling ? 'default' : 'pointer',
                userSelect: 'none', opacity: isToggling ? 0.6 : 1,
              }}
            >
              {p.activa ? 'Activa' : 'Inactiva'}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: '', key: 'acciones', width: 80, align: 'center',
      render: (_, p) => (
        <Space size={6}>
          <CircleBtn icon={<EditOutlined />} title="Editar" onClick={() => navigate(clientPath(`/admin/promociones/${p.id}`))} />
          <CircleBtn icon={<DeleteOutlined />} title="Eliminar" danger onClick={() => setConfirmDel(p)} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
        <Text strong style={{ fontSize: 28, lineHeight: 1.2 }}>Promociones</Text>
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => navigate(clientPath('/admin/promociones/nueva'))}
          style={{ background: primary, borderColor: primary, color: primaryText }}>
          Nueva promoción
        </Button>
      </div>

      <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 20 }}>
        Configurá promociones para tus clientes: envío gratis, descuentos fijos o porcentuales en compras que superen un monto mínimo.
      </Text>

      <SkStyleTag />
      <Table
        dataSource={loading ? skeletonRows(9) : promos}
        rowKey={loading ? 'key' : 'id'}
        pagination={false}
        locale={{ emptyText: (
          <div style={{ padding: '32px 0' }}>
            <GiftOutlined style={{ fontSize: 36, color: '#d1d5db', marginBottom: 12, display: 'block' }} />
            <Text type="secondary" style={{ fontSize: 14 }}>
              No hay promociones todavía. Creá una para ofrecer descuentos o envío gratis.
            </Text>
          </div>
        )}}
        columns={loading ? skeletonColumns(promoColumns, { avatarIndex: 0, twoLineIndexes: [0] }) : promoColumns}
        style={{ background: '#fff', borderRadius: 8 }}
      />

      <Modal
        open={!!confirmDel}
        title="¿Eliminar promoción?"
        onCancel={() => !deleting && setConfirmDel(null)}
        footer={
          <Space>
            <Button onClick={() => setConfirmDel(null)} disabled={deleting}>Cancelar</Button>
            <Button danger onClick={handleDelete} loading={deleting}>
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </Space>
        }
      >
        {confirmDel && (
          <Text>
            <strong>{generarNombre(tipos.find(t => t.id === confirmDel.id_promocion), confirmDel.valor, confirmDel.monto_minimo)}</strong> será eliminada.
          </Text>
        )}
      </Modal>
    </div>
  );
};
